import { Model } from 'app/model/backendmodel';
import { Trigger } from 'app/model/trigger/trigger';
import { ScheduleTriggerProperties, TriggerFrequency } from 'app/model/trigger/schedule-trigger-properties';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { AdapterRegistry } from '../adapter-registry';
import {
    AdapterContext,
    CustomEventsTriggerProperties,
    getIdentity,
    NamedAsyncAdapter,
    Owned,
    Tag
} from 'shell-models-lib';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { RecurrenceDay, RecurrenceSchedule } from 'app/model/trigger/recurrence-schedule';
import { RecurrenceScheduleOcccurence } from 'app/model/trigger/recurrence-schedule-occurence';
import { ParameterValue } from 'app/model/base/parameter-value';
import { Parameter } from 'app/model/base/parameter';
import { TumblingWindowTriggerProperties } from 'app/model/trigger/tumbling-window-trigger-properties';
import { TriggerPipeline } from 'app/model/trigger/trigger-pipeline';
import { RetryPolicy } from 'app/model/trigger/tumbling-window-retry-policy';
import { convertParameterValue, stringifyParameterValue } from 'app/shared/utils/parameter-converter.util';
import { stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { TriggerProperties } from 'app/model/trigger/trigger-properties';
import { EventTriggerProperties, TriggerEvent } from 'app/model/trigger/event-trigger-properties';
import { TriggerDependency } from 'app/model/trigger/trigger-dependency';
import { ChainingTriggerProperties } from 'app/model/trigger/chaining-trigger-properties';
import { RerunTumblingWindowTriggerProperties } from 'app/model/trigger/rerun-tumbling-window-trigger-properties';
import { GlobalVariables } from 'app/services/globals';
import { isRelativeUTCTime } from 'app/shared/utils/timezones.utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Trigger),
    asynchronous: true
})
export class TriggerAdapter extends NamedAsyncAdapter<Trigger, Model.TriggerResource> {
    protected override async _toResource(trigger: Trigger): Promise<Model.TriggerResource> {
        const triggerResource = (await super._toResource(trigger)) as Model.TriggerResource;

        this.properties(triggerResource).annotations = [];
        const tagAdapter = this.context.adapterFor(Tag.MODEL);
        for (const tagNamed of trigger.tags) {
            // do not save it if user created an empty tag
            const tagResource = tagAdapter.toResource(tagNamed);
            if (tagResource) {
                this.properties(triggerResource).annotations.push(tagResource);
            }
        }
        for (const tagNamed of trigger.malformedTags) {
            const tagResource = tagAdapter.toResource(tagNamed);
            if (tagResource) {
                this.properties(trigger).annotations.push(tagResource);
            }
        }

        const properties = this.properties(triggerResource) as Model.Trigger;
        properties.runtimeState = trigger.isActivated ? 'Started' : 'Stopped';

        if (!trigger.unknownType) {
            if (trigger.properties.multiplePipelines) {
                this._addMultiplePipelines(trigger, properties);
            } else {
                const pipelineIterator = trigger.pipelines.values();
                const item = pipelineIterator.next();
                // There's the case where the pipeline referenced in the JSON editor does not exist (if manually edited), so the pipeline value will be undefined.
                // We cannot validate that the pipeline exists before since we use proxy models at the moment of adding the dependency in the JSON editor
                // Check that the referenced pipeline exists before we attempt to get it, if is invalid, reset the pipelines array (this is only for TWT which can have at most 1 linked pipeline).
                if (!item.done) {
                    if (item.value.pipeline) {
                        (properties as Model.TumblingWindowTrigger).pipeline = this.getTriggerPipelineReference(
                            item.value
                        );
                    } else {
                        trigger.pipelines.length = 0;
                    }
                }
            }

            if (Metamodel.modelOf(trigger.properties) === ScheduleTriggerProperties.MODEL) {
                properties.type = ScheduleTriggerProperties.MODEL.name;
                (properties as Model.ScheduleTrigger).typeProperties = this.scheduleTriggerToResource(
                    trigger.properties as ScheduleTriggerProperties
                );
            } else if (Metamodel.modelOf(trigger.properties) === TumblingWindowTriggerProperties.MODEL) {
                properties.type = TumblingWindowTriggerProperties.MODEL.name;
                (properties as Model.TumblingWindowTrigger).typeProperties = this.tumblingWindowTriggerToResource(
                    trigger.properties as TumblingWindowTriggerProperties,
                    trigger.name
                );
            } else if (Metamodel.modelOf(trigger.properties) === EventTriggerProperties.MODEL) {
                properties.type = EventTriggerProperties.MODEL.name;
                (properties as Model.BlobEventsTrigger).typeProperties = this.blobEventsTriggerToResource(
                    trigger.properties as EventTriggerProperties
                );
            } else if (Metamodel.modelOf(trigger.properties) === ChainingTriggerProperties.MODEL) {
                properties.type = ChainingTriggerProperties.MODEL.name;
                (properties as Model.ChainingTrigger).typeProperties = this.chainingTriggerToResource(
                    trigger.properties as ChainingTriggerProperties
                );
            } else if (Metamodel.modelOf(trigger.properties) === RerunTumblingWindowTriggerProperties.MODEL) {
                properties.type = RerunTumblingWindowTriggerProperties.MODEL.name;
                (properties as Model.RerunTumblingWindowTrigger).typeProperties =
                    this.rerunTumblingWindowTriggerToResource(
                        trigger.properties as RerunTumblingWindowTriggerProperties
                    );
            } else if (Metamodel.modelOf(trigger.properties) === CustomEventsTriggerProperties.MODEL) {
                properties.type = CustomEventsTriggerProperties.MODEL.name;
                (properties as Model.CustomEventsTrigger).typeProperties = this.customEventsTriggerToResource(
                    trigger.properties as CustomEventsTriggerProperties
                );
            }
        } else {
            if (trigger.raw) {
                // TODO - ans - Currently only tumbling window has single pipeline otheriwse it is multiple pipelines.
                this._addMultiplePipelines(trigger, properties);
                const parsed = JSON.parse(trigger.raw);
                _.merge(triggerResource, parsed);
            }
        }
        return triggerResource;
    }

    protected override async _toModel(
        modelTriggerResource: Model.TriggerResource,
        trigger?: Trigger
    ): Promise<Trigger> {
        if (!trigger) {
            trigger = new Trigger();
        }
        await super._toModel(modelTriggerResource, trigger);

        if (modelTriggerResource.properties.annotations) {
            const tagAdapter = this.context.adapterFor(Tag.MODEL);
            for (const tagNamed of modelTriggerResource.properties.annotations) {
                if (typeof tagNamed === 'string') {
                    trigger.tags.add(tagAdapter.toModel(tagNamed));
                } else {
                    trigger.malformedTags.add(tagAdapter.toModel(tagNamed));
                }
            }
        }

        const properties = modelTriggerResource.properties;
        if (properties) {
            trigger.isActivated = stringsAreEqualCaseInsensitive(properties.runtimeState, 'Started');

            trigger.pipelines.length = 0;
            if (properties.pipelines) {
                properties.pipelines.forEach((pipelineReference) => {
                    if (trigger) {
                        this.addTriggerPipelineToModel(trigger, pipelineReference);
                    }
                });
            } else if (
                (properties as Model.TumblingWindowTrigger).pipeline &&
                (properties as Model.TumblingWindowTrigger).pipeline.pipelineReference
            ) {
                this.addTriggerPipelineToModel(trigger, (properties as Model.TumblingWindowTrigger).pipeline);
            }

            if (properties.type === ScheduleTriggerProperties.MODEL.name) {
                if (
                    (properties as Model.ScheduleTrigger).typeProperties &&
                    (properties as Model.ScheduleTrigger).typeProperties.recurrence
                ) {
                    const recurrence = (properties as Model.ScheduleTrigger).typeProperties.recurrence;
                    const recurrenceSched = this.scheduleTriggerRecurrenceToModel(recurrence.schedule);
                    // start and end time values are not linked to timezone when set by UX. Associate as UTC universally
                    // add 'Z' suffix if not already there
                    let startTimeUTC;
                    let endTimeUTC;
                    if (GlobalVariables.disableRelativeUTCTimezoneFormat) {
                        startTimeUTC = recurrence.startTime.endsWith('Z')
                            ? new Date(recurrence.startTime)
                            : new Date(`${recurrence.startTime}Z`);
                        endTimeUTC = recurrence.endTime
                            ? recurrence.endTime.endsWith('Z')
                                ? new Date(recurrence.endTime)
                                : new Date(`${recurrence.endTime}Z`)
                            : undefined;
                    } else {
                        startTimeUTC =
                            recurrence.startTime.endsWith('Z') || isRelativeUTCTime(recurrence.startTime)
                                ? new Date(recurrence.startTime)
                                : new Date(`${recurrence.startTime}Z`);
                        endTimeUTC = recurrence.endTime
                            ? recurrence.endTime.endsWith('Z') || isRelativeUTCTime(recurrence.endTime)
                                ? new Date(recurrence.endTime)
                                : new Date(`${recurrence.endTime}Z`)
                            : undefined;
                    }
                    trigger.createScheduledTrigger(
                        recurrence.frequency as TriggerFrequency,
                        startTimeUTC,
                        recurrence.timeZone || 'UTC',
                        recurrence.interval as number,
                        endTimeUTC,
                        recurrenceSched
                    );
                } else {
                    trigger.properties = Owned.create<ScheduleTriggerProperties>(
                        ScheduleTriggerProperties.MODEL,
                        trigger
                    );
                }
            } else if (properties.type === TumblingWindowTriggerProperties.MODEL.name) {
                const typeProperties = (properties as Model.TumblingWindowTrigger).typeProperties;
                let dependsOn: TriggerDependency[];
                if (typeProperties) {
                    if (typeProperties.dependsOn) {
                        dependsOn = this.getTriggerReferencesToTumblingWindow(typeProperties.dependsOn, trigger.name);
                    }
                    // Add 'Z' suffix if not already there
                    const startTimeUTC = typeProperties.startTime.endsWith('Z')
                        ? new Date(typeProperties.startTime)
                        : new Date(`${typeProperties.startTime}Z`);
                    const endTimeUTC = typeProperties.endTime
                        ? typeProperties.endTime.endsWith('Z')
                            ? new Date(typeProperties.endTime)
                            : new Date(`${typeProperties.endTime}Z`)
                        : undefined;
                    trigger.createTumblingWindowTrigger(
                        typeProperties.frequency as TriggerFrequency,
                        startTimeUTC,
                        typeProperties.interval as number,
                        typeProperties.delay,
                        typeProperties.maxConcurrency as number,
                        endTimeUTC,
                        this.retryPolicyToModel(typeProperties.retryPolicy),
                        dependsOn
                    );
                } else {
                    trigger.properties = Owned.create<TumblingWindowTriggerProperties>(
                        TumblingWindowTriggerProperties.MODEL,
                        trigger
                    );
                }
            } else if (properties.type === EventTriggerProperties.MODEL.name) {
                const typeProperties = (properties as Model.BlobEventsTrigger).typeProperties;
                if (typeProperties) {
                    trigger.createBlobEventsTrigger(
                        typeProperties.scope as string,
                        typeProperties.tenantId,
                        typeProperties.events as TriggerEvent[],
                        typeProperties.blobPathBeginsWith,
                        typeProperties.blobPathEndsWith,
                        typeProperties.ignoreEmptyBlobs,
                        typeProperties.maxConcurrency
                    );
                } else {
                    trigger.properties = Owned.create<EventTriggerProperties>(EventTriggerProperties.MODEL, trigger);
                }
            } else if (properties.type === CustomEventsTriggerProperties.MODEL.name) {
                const typeProperties = (properties as Model.CustomEventsTrigger).typeProperties;
                if (typeProperties) {
                    trigger.createCustomEventsTrigger(
                        typeProperties.scope as string,
                        typeProperties.tenantId,
                        typeProperties.events as string[],
                        typeProperties.subjectBeginsWith,
                        typeProperties.subjectEndsWith,
                        typeProperties.advancedFilters
                    );
                } else {
                    trigger.properties = Owned.create<CustomEventsTriggerProperties>(
                        CustomEventsTriggerProperties.MODEL,
                        trigger
                    );
                }
            } else if (properties.type === ChainingTriggerProperties.MODEL.name) {
                const typeProperties = (properties as Model.ChainingTrigger).typeProperties;
                if (typeProperties) {
                    trigger.createChainingTrigger(
                        typeProperties.dependsOn.map((pipelineReference: Model.PipelineReference) =>
                            ReferenceAdapter.toProxyModel<Pipeline>(
                                Metamodel.modelFor(Pipeline),
                                pipelineReference as Model.PipelineReference, this.context
                            )
                        ),
                        typeProperties.runDimension
                    );
                } else {
                    trigger.properties = Owned.create<ChainingTriggerProperties>(
                        ChainingTriggerProperties.MODEL,
                        trigger
                    );
                }
            } else if (properties.type === RerunTumblingWindowTriggerProperties.MODEL.name) {
                const typeProperties = (properties as Model.RerunTumblingWindowTrigger).typeProperties;
                typeProperties.requestedStartTime = new Date(typeProperties.requestedStartTime);
                typeProperties.requestedEndTime = new Date(typeProperties.requestedEndTime);
                if (typeProperties) {
                    trigger.createRerunRangeTumblingWindowTrigger(
                        typeProperties.parentTrigger.referenceName,
                        typeProperties.requestedStartTime,
                        typeProperties.requestedEndTime,
                        typeProperties.rerunConcurrency
                    );
                } else {
                    trigger.properties = Owned.create<RerunTumblingWindowTriggerProperties>(
                        RerunTumblingWindowTriggerProperties.MODEL,
                        trigger
                    );
                }
            } else {
                let model: IModel | undefined;
                try {
                    model = Metamodel.modelByName(properties.type);
                } catch {
                    model = Metamodel.createModel(TriggerProperties.MODEL, {
                        name: properties.type,
                        display: [properties.type],
                        createdFromUnknownType: true
                    });
                }

                trigger.properties = model.create();
                trigger.unknownType = true;
                trigger.raw = JSON.stringify(
                    await ValueAdapter.remainderWithExternalSpec(this.context, modelTriggerResource, {
                        name: '*',
                        properties: {
                            description: '*',
                            runtimeState: '*',
                            pipelines: '*'
                        }
                    })
                );
            }
        }

        return trigger;
    }

    private _addMultiplePipelines(trigger: Trigger, properties: Model.Trigger): void {
        const pipelineReferences: Model.TriggerPipelineReference[] = [];
        trigger.pipelines.forEach(
            (pipeline) => pipeline.pipeline && pipelineReferences.push(this.getTriggerPipelineReference(pipeline))
        );
        properties.pipelines = pipelineReferences;
    }

    private getTumblingWindowReferences(
        typeProperties: TumblingWindowTriggerProperties,
        parentTriggerName: string
    ): Model.TriggerDependency[] {
        let dependsOn: Model.TriggerDependency[] = [];
        if (typeProperties.dependsOn) {
            dependsOn = typeProperties.dependsOn
                .map((item) => {
                    let triggerReference: Model.TriggerReference;
                    const triggerDependency: Model.TriggerDependency = {
                        type: 'SelfDependencyTumblingWindowTriggerReference',
                        size: item.windowSize ? item.windowSize : undefined,
                        offset: item.offset ? item.offset : undefined
                    };
                    if (item.trigger && item.trigger.name !== parentTriggerName) {
                        triggerReference = ReferenceAdapter.toResource(
                            item.trigger,
                            this.context
                        ) as Model.TriggerReference;
                        triggerDependency.referenceTrigger = triggerReference;
                        triggerDependency.type = 'TumblingWindowTriggerDependencyReference';
                    }
                    return triggerDependency;
                })
                .filter((item) => item !== undefined);
        }
        return dependsOn;
    }

    private getTriggerPipelineReference(pipeline: TriggerPipeline): Model.TriggerPipelineReference {
        const pipelineReference = ReferenceAdapter.toResource(
            pipeline.pipeline,
            this.context
        ) as Model.PipelineReference;
        const parameters: { [propertyName: string]: {} } = {};
        pipeline.parameters.forEach((param) => {
            if (param.parameter) {
                parameters[this.context.identityProvider.identity(param.parameter)] = convertParameterValue(
                    param.value,
                    param.parameter._type,
                    'TriggerAdapter',
                    this.ensureLogger()
                );
            }
        });

        return {
            pipelineReference,
            parameters
        };
    }

    private scheduleTriggerToResource(properties: ScheduleTriggerProperties): Model.ScheduleTriggerTypeProperties {
        const resourceSchedule: Model.ScheduleTriggerTypeProperties = {};
        // Remove milliseconds from ISO string using split()
        const dateSuffix = properties.timeZone === 'UTC' ? 'Z' : '';
        const startTime: string = `${properties.startTime.toISOString().split('.')[0]}${dateSuffix}`;
        const endTime: string = properties.endTime
            ? `${properties.endTime.toISOString().split('.')[0]}${dateSuffix}`
            : undefined;
        resourceSchedule.recurrence = {
            frequency: properties.frequency,
            interval: properties.interval,
            startTime,
            endTime,
            timeZone: properties.timeZone,
            schedule: this.scheduleTriggerRecurrenceToResource(properties.schedule)
        };
        return resourceSchedule;
    }

    private tumblingWindowTriggerToResource(
        properties: TumblingWindowTriggerProperties,
        parentTriggerName: string
    ): Model.TumblingWindowTriggerTypeProperties {
        let retryPolicy: Model.RetryPolicy = {};
        if (properties.retryPolicy) {
            retryPolicy = {
                count: properties.retryPolicy.count,
                intervalInSeconds: properties.retryPolicy.interval
            };
        }
        let dependsOn: Model.TriggerDependency[];
        if (properties.dependsOn) {
            dependsOn = this.getTumblingWindowReferences(properties, parentTriggerName);
        }

        // Remove milliseconds from ISO string using split()
        const startTime: string = `${properties.startTime.toISOString().split('.')[0]}Z`;
        const endTime: string = properties.endTime ? `${properties.endTime.toISOString().split('.')[0]}Z` : undefined;

        return {
            frequency: properties.frequency,
            interval: properties.interval,
            startTime,
            endTime,
            delay: properties.delay,
            maxConcurrency: properties.maxConcurrency,
            retryPolicy,
            dependsOn
        };
    }

    private blobEventsTriggerToResource(properties: EventTriggerProperties): Model.BlobEventsTriggerTypeProperties {
        return {
            blobPathBeginsWith: properties.blobPathBeginsWith,
            blobPathEndsWith: properties.blobPathEndsWith,
            ignoreEmptyBlobs: properties.ignoreEmptyBlobs,
            maxConcurrency: properties.maxConcurrency,
            scope: properties.scope,
            tenantId: properties.tenantId,
            events: properties.events
        };
    }

    private customEventsTriggerToResource(
        properties: CustomEventsTriggerProperties
    ): Model.CustomEventsTriggerTypeProperties {
        const customEventTrigger: Model.CustomEventsTriggerTypeProperties = {
            subjectEndsWith: properties.subjectEndsWith,
            subjectBeginsWith: properties.subjectBeginsWith,
            scope: properties.scope,
            tenantId: properties.tenantId,
            events: properties.events
        };

        if (properties.advancedFilters && properties.advancedFilters.length) {
            customEventTrigger.advancedFilters = properties.advancedFilters.filter((advancedFilter) => {
                return (
                    advancedFilter.key && advancedFilter.operatorType && (advancedFilter.value || advancedFilter.values)
                );
            });
        }
        return customEventTrigger;
    }

    private chainingTriggerToResource(properties: ChainingTriggerProperties): Model.ChainingTriggerTypeProperties {
        return {
            dependsOn: properties.dependsOn.map(
                (pipeline) => ReferenceAdapter.toResource(pipeline, this.context) as Model.PipelineReference
            ),
            runDimension: properties.runDimension
        };
    }

    private rerunTumblingWindowTriggerToResource(
        properties: RerunTumblingWindowTriggerProperties
    ): Model.RerunTumblingWindowTriggerTypeProperties {
        const parentTriggerReference = { referenceName: properties.parentTrigger, type: 'TriggerReference' };
        return {
            parentTrigger: parentTriggerReference,
            requestedStartTime: properties.requestedStartTime,
            requestedEndTime: properties.requestedEndTime,
            rerunConcurrency: properties.rerunConcurrency
        };
    }

    private scheduleTriggerRecurrenceToResource(
        recurrenceProp?: RecurrenceSchedule
    ): Model.RecurrenceSchedule | undefined {
        if (!recurrenceProp) {
            return undefined;
        }
        const monthlyOccurence: Model.RecurrenceScheduleOccurrence[] = [];
        if (recurrenceProp.monthlyOccurrences.length) {
            for (const item of recurrenceProp.monthlyOccurrences) {
                const occurence: Model.RecurrenceScheduleOccurrence = {
                    day: item.day,
                    occurrence: item.occurrence
                };
                monthlyOccurence.push(occurence);
            }
        }
        return {
            minutes: recurrenceProp.minutes.length ? recurrenceProp.minutes : undefined,
            hours: recurrenceProp.hours.length ? recurrenceProp.hours : undefined,
            weekDays: recurrenceProp.weekDays.length ? recurrenceProp.weekDays : undefined,
            monthDays: recurrenceProp.monthDays.length ? recurrenceProp.monthDays : undefined,
            monthlyOccurrences: recurrenceProp.monthlyOccurrences.length ? monthlyOccurence : undefined
        };
    }

    private scheduleTriggerRecurrenceToModel(
        recurrenceModel?: Model.RecurrenceSchedule
    ): RecurrenceSchedule | undefined {
        if (!recurrenceModel) {
            return undefined;
        }
        const recurrenceProp = Metamodel.modelFor(RecurrenceSchedule).create<RecurrenceSchedule>();
        recurrenceProp.hours = recurrenceModel.hours || [];
        recurrenceProp.minutes = recurrenceModel.minutes || [];
        recurrenceProp.monthDays = recurrenceModel.monthDays || [];
        recurrenceProp.weekDays = (recurrenceModel.weekDays as RecurrenceDay[]) || [];
        recurrenceProp.monthlyOccurrences = [];
        for (const monthlyOccurence of recurrenceModel.monthlyOccurrences || []) {
            if (monthlyOccurence.day && monthlyOccurence.occurrence) {
                const monthlyProp = new RecurrenceScheduleOcccurence(
                    monthlyOccurence.day as RecurrenceDay,
                    monthlyOccurence.occurrence
                );
                recurrenceProp.monthlyOccurrences.push(monthlyProp);
            }
        }

        return recurrenceProp;
    }

    private retryPolicyToModel(retryPolicy?: Model.RetryPolicy): RetryPolicy | undefined {
        if (!retryPolicy) {
            return undefined;
        }
        return {
            count: retryPolicy.count,
            interval: retryPolicy.intervalInSeconds
        };
    }

    private getTriggerReferencesToTumblingWindow(
        twTriggerReferences: Model.TriggerDependency[],
        parentTriggerName: string
    ): TriggerDependency[] {
        let triggerDependencies: TriggerDependency[] = [];
        if (twTriggerReferences) {
            triggerDependencies = twTriggerReferences.map((item) => {
                let trigger: Trigger = null;
                if (item.referenceTrigger) {
                    trigger = ReferenceAdapter.toProxyModel<Trigger>(
                        Metamodel.modelFor(Trigger),
                        item.referenceTrigger as Model.TriggerReference, this.context
                    );
                } else {
                    const parentTriggerReference = { referenceName: parentTriggerName, type: 'TriggerReference' };
                    trigger = ReferenceAdapter.toProxyModel<Trigger>(
                        Metamodel.modelFor(Trigger),
                        parentTriggerReference as Model.TriggerReference, this.context
                    );
                }
                return new TriggerDependency(trigger, item.offset, item.size);
            });
        }
        return triggerDependencies;
    }

    private addTriggerPipelineToModel(trigger: Trigger, pipelineReference: Model.TriggerPipelineReference): void {
        if (!pipelineReference.pipelineReference) {
            return;
        }
        const pipeline = ReferenceAdapter.toProxyModel<Pipeline>(
            Metamodel.modelFor(Pipeline),
            pipelineReference.pipelineReference as Model.PipelineReference, this.context
        );
        const triggerParameters = this.convertParametersToModel(pipeline, pipelineReference);
        trigger.addPipeline(pipeline, triggerParameters);
    }

    private convertParametersToModel(
        pipeline: Pipeline,
        pipelineReference: Model.TriggerPipelineReference
    ): ParameterValue[] {
        const resourceParameters = pipelineReference.parameters;
        const triggerParameters: ParameterValue[] = [];
        if (resourceParameters) {
            Object.keys(resourceParameters).map((parameterIdentity) => {
                const parameterValue = ParameterValue.MODEL.create<ParameterValue>();
                parameterValue.value = stringifyParameterValue(resourceParameters[parameterIdentity]);
                if (this.context.adapterConfig.replaceIdentitiesForNonConcepts) {
                    parameterIdentity = AdapterContext.getIdentity(getIdentity(pipeline), parameterIdentity);
                }
                const parameter = ReferenceAdapter.toProxyReferenceModel<Parameter>(
                    Metamodel.modelFor(Parameter),
                    parameterIdentity
                );
                parameterValue.parameter = parameter;
                pipeline.parameters.add(parameter);
                triggerParameters.push(parameterValue);
            });
        }
        return triggerParameters;
    }
}
